import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Link } from 'react-router-dom';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import { DocumentStatus } from '@/types';
import ProjectDocuments from './ProjectDocuments';
import UnitDocuments from './UnitDocuments';
import { searchProjectDocuments, searchResidentialUnitDocuments } from '@/services/documents';
import { Tabs } from '@ygtechnologies/rubus-ui';

type TabId = 'documents' | 'project-documents';
interface TabSetup {
    id: TabId;
    name: ReactElement;
    render: (options: { projectId: number }) => ReactElement;
    notificationCount?: number;
}

interface DocumentsPageProps {
    tab: TabId;
    projectId: string | number;
}

const DocumentsPage: React.FC<DocumentsPageProps> = ({ projectId, tab }) => {
    const [unitPendingCount, setUnitPendingCount] = useState<number>(0);
    const [projectPendingCount, setProjectPendingCount] = useState<number>(0);

    // Fetch pending counts for both tabs
    useEffect(() => {
        const fetchPendingCounts = async () => {
            const [unitPendingDocs, projectPendingDocs] = await Promise.all([
                searchResidentialUnitDocuments({
                    projectId: +projectId,
                    excludeProjectDocuments: true,
                    includeArchived: false,
                    status: [DocumentStatus.Pending],
                    includeUnpublishedVersionsCount: true,
                }),
                searchProjectDocuments({
                    projectId: +projectId,
                    includeArchived: false,
                    status: [DocumentStatus.Pending],
                    IncludeUnpublishedVersionsCount: true,
                }),
            ]);

            setUnitPendingCount(unitPendingDocs.length);
            setProjectPendingCount(projectPendingDocs.length);
        };

        fetchPendingCounts();
    }, [projectId]);

    const tabs: TabSetup[] = useMemo(
        () => [
            {
                id: 'documents',
                name: <Trans>Unit documents</Trans>,
                render: (options) => <UnitDocuments {...options} />,
                notificationCount: unitPendingCount > 0 ? unitPendingCount : undefined,
            },
            {
                id: 'project-documents',
                name: <Trans>Project documents</Trans>,
                render: (options) => <ProjectDocuments {...options} />,
                notificationCount: projectPendingCount > 0 ? projectPendingCount : undefined,
            },
        ],
        [unitPendingCount, projectPendingCount]
    );

    const activeTab = tabs.find((x) => x.id === tab);

    return (
        <>
            <SimplePageTitle title={t`Documents`} />

            <Tabs.Container activeId={tab} variant="underline" sticky stickyClassName="stickyBleed">
                {tabs.map((t) => (
                    <Tabs.Tab
                        key={t.id}
                        id={t.id}
                        as={Link}
                        to={`/projects/${projectId}/${t.id}`}
                        label={t.name}
                        badge={t.notificationCount}
                    />
                ))}
                <Tabs.Content>{activeTab?.render({ projectId: +projectId })}</Tabs.Content>
            </Tabs.Container>
        </>
    );
};

export default DocumentsPage;
